import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMount } from 'react-use'
import { match, P } from 'ts-pattern'
import { z } from 'zod'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { surveyFilterAtom } from '~/atoms'
import { LoadingPanel } from '~/components/elements/LoadingPanel'
import { ErrorPanel } from '~/components/features/ErrorPanel'
import { graphql } from '~/gql'
import { checkCurrentSurvey } from '~/hoc/checkCurrentSurvey'
import { useFetchSurveyQuery } from '~/hooks/useSurveyQuery'
import { useTabulate } from '~/hooks/useTabulate'
import {
  conditionOperations,
  exists,
  surveyFilterGroupsSchema,
  toSurveyFilterV2,
} from '~/utils'

const countTotalAnswersMutationDocument = graphql(`
  mutation RestoreSurveyFilterPage_CountTotalAnswersMutation(
    $surveyId: String!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countTotalAnswers(
      surveyId: $surveyId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const countTotalAnswersQueryDocument = graphql(`
  query RestoreSurveyFilterPage_CountTotalAnswers($awaitId: String!) {
    count: countTotalAnswers(awaitId: $awaitId) {
      arrived
      result {
        count
      }
    }
  }
`)

type RestoringState = 'complete' | 'invalidParams' | 'noParams' | 'processing'

const schema = z.object({
  replace: z.object({ path: z.string(), state: z.unknown() }),
  surveyFilter: z.union([
    z.object({
      surveyId: z.string(),
      filterGroups: surveyFilterGroupsSchema,
    }),
    z.object({
      surveyId: z.string(),
      filterGroups: z.undefined(),
      questionFilters: z
        .array(
          z.object({
            operation: z.enum(conditionOperations),
            question_id: z.string(),
            values: z.array(z.number()),
          }),
        )
        .optional()
        .default([]),
      segmentFilters: z
        .array(
          z.object({
            brand_group_id: z.number(),
            brand_number: z.number(),
            segment_number: z.number(),
          }),
        )
        .optional()
        .default([]),
    }),
  ]),
})

const backLinkPath = compile('top', {})

export const RestoreSurveyFilterPage = checkCurrentSurvey(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const [restoringState, setRestoringState] =
    React.useState<RestoringState>('processing')

  const [_countTotalAnswersState, countTotalAnswers] = useTabulate(
    countTotalAnswersMutationDocument,
    countTotalAnswersQueryDocument,
  )

  const setSurveyFilter = useSetAtom(surveyFilterAtom)

  const fetchSurveyQuery = useFetchSurveyQuery()

  useMount(async () => {
    const state = location.state

    if (!exists(state)) {
      setRestoringState('noParams')

      return
    }

    const parsed = schema.safeParse(state)
    if (!parsed.success) {
      setRestoringState('invalidParams')

      return
    }

    const { replace, surveyFilter } = parsed.data

    const surveyId = surveyFilter.surveyId

    const filterGroups = await match(surveyFilter)
      .with(
        {
          filterGroups: P.not(P.nullish),
        },
        ({ filterGroups }) => filterGroups,
      )
      .with(
        {
          questionFilters: P.not(P.nullish),
          segmentFilters: P.not(P.nullish),
        },
        async (surveyFilter) => {
          const data = await fetchSurveyQuery()
          const questions = data.survey?.questions?.filter(exists) ?? []

          return toSurveyFilterV2(surveyFilter, questions)
        },
      )
      .exhaustive()

    if (0 < filterGroups.length) {
      const result = await countTotalAnswers({ surveyId, filter: filterGroups })

      setSurveyFilter({ count: result?.count ?? NaN, filterGroups })
    } else {
      setSurveyFilter(undefined)
    }

    navigate(replace.path, { state: replace.state, replace: true })

    setRestoringState('complete')
  })

  return match(restoringState)
    .with('processing', () => <LoadingPanel containerSize="full" />)
    .with('noParams', () => (
      <ErrorPanel
        backLinkPath={backLinkPath}
        message={t('this_page_cannot_be_accessed_directly')}
      />
    ))
    .with('invalidParams', () => (
      <ErrorPanel
        backLinkPath={backLinkPath}
        message={t('invalid_access_detected')}
      />
    ))
    .with('complete', () => null)
    .exhaustive()
})
